/**
 * THE COC FOLLOW-UPS WORK AREA -- pure view-model (COC-EXPERIENCE-V1).
 *
 * The Follow-ups work area (ONE tab of the still-unbuilt COC command center;
 * #593 corrective item 3) over the merged #589 engine
 * (COC-FOLLOWUP-CORE-JOURNEY-V1). This module holds ONLY pure derivations of
 * the shipped #589 read carriers (PatientFollowupQueueView) -- no React, no
 * I/O -- so the workboard's summary math, queue filtering, row copy, and the
 * one-contact/multi-item recorder payload are unit-pinned without jsdom
 * (the repo's *-view.ts idiom: money-desk-view / workboard-view).
 *
 * TRUTH FENCES honored here by construction:
 *  - NO clinical narrative widens: this module reads ONLY ids, treatment
 *    names, clinic/doctor labels, estimate, age and the per-item follow-up
 *    state. It NEVER references chiefComplaintCategory / diagnosisCategory
 *    (on the wire, deliberately unread -- ADR-0019 clinical wire fence).
 *  - NO machine ids on the screen: no UMR, no patient uuid, no
 *    treatmentPlanItemId is ever rendered by the copy helpers (03-coc mockup
 *    identifier law: "no UMR (never on COC surfaces)"; identity = name +
 *    context line).
 *  - CONVERTED is never manufactured (RULING 2): the recorder vocabulary is
 *    exactly { open | declined | unreachable | cold }; COST/OTHER ride ONLY a
 *    decline (RULING 3).
 */

import type {
  CocFollowupDisposition,
  CocFollowupReason,
  CocItemFollowupState,
  CocItemTerminalState,
  PatientFollowupPlanItemView,
  PatientFollowupQueueView,
  PatientFollowupRowView,
  RecordCocFollowupItemArg,
} from '@/dashboard/api';
import type { AcBadgeTone } from '@/dashboard/ui/AcBadge';
import { formatIstShortDate, istCalendarDayYmd } from '@/lib/datetime-ist';
import { outcomeReasonLabel } from '@/dashboard/owner/Lead360Panel';
import { planItemStatusLabel } from '@/dashboard/clinical-workbench/case-sheet-labels';
import { rupeesLabel } from '@/dashboard/doctor/doctor-consult-view';
import {
  conversionStateLabel,
  treatmentHighlightTone,
  type CommercialConversionStateWire,
} from '@/dashboard/patients/patient-360-view';

/**
 * The first-follow-up SLA (current CONFIRMED truth,
 * COC-CSD-WORKFLOW-AND-FOLLOWUP-2026-07-08): a treatment is advised ->
 * first follow-up is expected within 1 day -> no attempt by the END of the
 * next day is overdue/missed. `ageDays` counts whole days since the oldest
 * open item was advised, so `>= 2` means that next day has fully elapsed with
 * no recorded contact.
 *
 * SUBSEQUENT follow-up timing is now RULED (Deepak 2026-08-21 item 8,
 * superseding the OPEN-question note this header used to carry): the next
 * follow-up is per treatment item, a CUSTOM date the COC hand-picks --
 * recorded via the contact recorder and read back per item
 * (FOLLOWUP-DUE-DATE-PLUMBING-V1; migration 20260826_1). Still NO cadence:
 * nothing here computes, defaults or suggests a date -- the due-state
 * helpers below only compare the human's own recorded day to the IST
 * calendar day.
 */
/**
 * FIRST-FOLLOWUP-SLA-KEY-V1 (founder ruling 2026-09-01 on PR #674): this
 * module NO LONGER DECLARES the SLA number. It used to carry
 * `FIRST_FOLLOWUP_SLA_DAYS = 2`, hand-kept in step with an identical
 * literal in lib/domain/patient/followup-report.ts -- two copies of the
 * number that decides who is late, which would have drifted silently and
 * made this list and the clinic report disagree with no error anywhere.
 *
 * The number now travels on the wire, in the ownership config block the
 * server already sends. There is deliberately NO fallback constant here: a
 * default in this file would be the second copy again. When the server
 * sent no config block at all (an older payload), the answer is NULL --
 * "we were not told the rule" -- and nothing is called overdue on a number
 * nobody gave us. In practice the branch is unreachable: the backend fills
 * the block on every response.
 */
export function firstFollowupSlaDaysOf(
  view: Pick<PatientFollowupQueueView, 'followupOwnership'>,
): number | null {
  return view.followupOwnership?.firstFollowupSlaDays ?? null;
}

/** Which work lens the summary band is filtering the queue to.
 *
 *  CSD IS RETIRED (founder ruling 2026-09-04, banked at
 *  docs/recon/GLOBAL-ROLE-CONFORMANCE-2026-09-04.md: "RECOVERY = COC ONLY.
 *  CSD IS RETIRED"). The 'csd' member is REMOVED from the vocabulary rather
 *  than left unreachable, so no caller can ask for a lens this screen no
 *  longer has. `csdHandoffState` stays on the WIRE untouched -- retiring a
 *  screen affordance is not a licence to change a server contract other
 *  readers still consume (lib/adapters/supabase/coc-followup-report-read-
 *  model.ts). */
export type FollowupFilter = 'all' | 'overdue' | 'newly';

/** The truthful work-summary facts -- each a COUNT derived from the shipped
 *  wire, never an invented metric (no conversion rate, no ticket size;
 *  founder rulings 2 + 11). THREE since the CSD retirement above: a count
 *  nothing on the glass can filter to is a number with no reader. */
export interface FollowupSummary {
  readonly needAction: number;
  readonly overdue: number;
  readonly newlyAdvised: number;
}

/** Per-item current follow-up state keyed by treatmentPlanItemId (RULING 2).
 *  The persistence carrier (migration 20260820_1) is FOUNDER-APPLIED
 *  (applied-state ledger, 2026-08-20); an item with no recorded contact simply
 *  has no entry here and reads OPEN with no attribution (honest, never a fake). */
export function statesByItemId(
  view: PatientFollowupQueueView,
): Map<number, CocItemFollowupState> {
  const byItem = new Map<number, CocItemFollowupState>();
  for (const s of view.itemFollowupStates ?? []) {
    byItem.set(s.treatmentPlanItemId, s);
  }
  return byItem;
}

/** The 3 CONVERTED rungs of the shared 9-token commercial-conversion ladder
 *  (patient-360-view.ts CommercialConversionStateWire) -- the only states
 *  this register renders a badge for (COC-RENDER-CLOSURE-V1, activity 20).
 *  The other 6 (undecided/declined/deferred/accepted-payment-pending/...)
 *  restate what the row's own open-item state already shows, so surfacing
 *  them here would be repeated dead prose (WORK-SURFACE-V1); absence reads
 *  as "not yet converted," matching this page's own Overdue/CSD idiom. */
const CONVERTED_STATES: ReadonlySet<CommercialConversionStateWire> = new Set([
  'converted_first_payment',
  'converted_chief_complaint',
  'converted_full_package',
]);

/** Patient id -> canonical conversion state, from the wire's BEST-EFFORT
 *  per-patient array (absent = the lane was unreadable for that patient,
 *  never "not converted" -- see PatientFollowupConversionStateView). */
export function conversionByPatientId(
  view: PatientFollowupQueueView,
): Map<string, CommercialConversionStateWire> {
  const out = new Map<string, CommercialConversionStateWire>();
  for (const c of view.conversionStates ?? []) out.set(c.patientId, c.state);
  return out;
}

/** This row's conversion state, ONLY when it is one of the 3 CONVERTED
 *  rungs (RULING 2: CONVERTED is payment-derived and COC-unwritable; this
 *  reads the canonical derivation, never fabricates it). Null otherwise --
 *  either not yet converted, or the lane could not be computed; the register
 *  never distinguishes the two (a silent badge, never a scary error, for a
 *  BEST-EFFORT lane that is not this row's primary job). */
export function rowConvertedState(
  row: PatientFollowupRowView,
  conversions: Map<string, CommercialConversionStateWire>,
): CommercialConversionStateWire | null {
  const state = conversions.get(row.patientId);
  return state !== undefined && CONVERTED_STATES.has(state) ? state : null;
}

export { conversionStateLabel, treatmentHighlightTone };

/** True once ANY of the row's open items carries a recorded contact -- the
 *  honest "first follow-up has happened" signal (false until a contact is
 *  recorded on the row). */
export function rowHasContact(
  row: PatientFollowupRowView,
  statesByItem: Map<number, CocItemFollowupState>,
): boolean {
  return row.openItems.some(
    (item) => statesByItem.get(item.id)?.lastContactAtIso != null,
  );
}

/** Past the first-follow-up SLA with no attempt yet (RULING: overdue is a
 *  DERIVED state, never an auto terminal disposition).
 *
 *  MIRRORS isFirstFollowupOverdue (lib/domain/patient/followup-ownership.ts)
 *  -- a mirror and not an import because this project's frontend tsconfig
 *  resolves `@/*` to `src/*` only, so backend domain code is unreachable
 *  from here. What is NOT mirrored is the NUMBER: both sides read the one
 *  org config value. `slaDays` NULL means the server sent no config block,
 *  so no row is called overdue. */
export function rowIsOverdue(
  row: PatientFollowupRowView,
  statesByItem: Map<number, CocItemFollowupState>,
  slaDays: number | null,
): boolean {
  if (slaDays === null) return false;
  return !rowHasContact(row, statesByItem) && row.ageDays >= slaDays;
}

/** Advised, still inside the first-follow-up window, no attempt yet. The
 *  complement of rowIsOverdue among uncontacted rows; with no configured
 *  SLA nothing is classified either way. */
export function rowIsNewlyAdvised(
  row: PatientFollowupRowView,
  statesByItem: Map<number, CocItemFollowupState>,
  slaDays: number | null,
): boolean {
  if (slaDays === null) return false;
  return !rowHasContact(row, statesByItem) && row.ageDays < slaDays;
}

/** The compact work-summary band facts (all derived; no invented KPI). */
export function summarizeFollowups(
  view: PatientFollowupQueueView,
): FollowupSummary {
  const statesByItem = statesByItemId(view);
  const slaDays = firstFollowupSlaDaysOf(view);
  let overdue = 0;
  let newlyAdvised = 0;
  for (const row of view.rows) {
    if (rowIsOverdue(row, statesByItem, slaDays)) overdue += 1;
    if (rowIsNewlyAdvised(row, statesByItem, slaDays)) newlyAdvised += 1;
  }
  return { needAction: view.rows.length, overdue, newlyAdvised };
}

/** The queue rows under the active summary lens (counters FILTER, never
 *  stack). `all` returns every row unchanged. */
export function filterFollowupRows(
  rows: readonly PatientFollowupRowView[],
  filter: FollowupFilter,
  statesByItem: Map<number, CocItemFollowupState>,
  slaDays: number | null,
): PatientFollowupRowView[] {
  switch (filter) {
    case 'overdue':
      return rows.filter((r) => rowIsOverdue(r, statesByItem, slaDays));
    case 'newly':
      return rows.filter((r) => rowIsNewlyAdvised(r, statesByItem, slaDays));
    case 'all':
    default:
      return [...rows];
  }
}

/** Whole-INR estimate in the canonical doctor money grammar ("Rs <en-IN
 *  digits>" -- rupeesLabel, DOCTOR-MONEY-CONFORMANCE-V1: this queue is a
 *  doctor window-read surface); null (no configured price) is honest,
 *  never a fake zero (the NULL-AMOUNT OBLIGATION LAW). */
export function formatEstimateInr(amount: number | null): string {
  return amount === null ? 'No estimate' : rupeesLabel(amount);
}

/** Total configured estimate across a row's open items, or null when NONE of
 *  them carries a price (never a fake zero). */
export function totalEstimateInr(
  items: readonly PatientFollowupPlanItemView[],
): number | null {
  let total = 0;
  let any = false;
  for (const item of items) {
    if (item.estimatedValue !== null) {
      total += item.estimatedValue;
      any = true;
    }
  }
  return any ? total : null;
}

/** ALL advised treatment names, comma-joined -- the full WHAT of the row, so a
 *  COC discovers the treatment SCOPE of the call BEFORE opening the drawer
 *  (#593 visual corrective item 8: clinically relevant scope is never hidden
 *  behind an opaque "+1 more"). Names ONLY -- never a clinical note, chief
 *  complaint, or diagnosis category (ADR-0019 wire fence), never a UMR / uuid /
 *  item id. Compact by construction (short procedure names). */
export function treatmentNames(
  items: readonly PatientFollowupPlanItemView[],
): string {
  if (items.length === 0) return 'Treatment advised';
  return items.map((item) => item.procedureName).join(', ');
}

/** R-COC-ADVISED-SCOPE-01 (COC-ADVISED-VISIBILITY-V1): this row's ADVISED
 *  (not-yet-decided) items -- READ-ONLY additive context, structurally
 *  distinct from `row.openItems` (never actionable, never part of the
 *  recorder). Absence on an older payload reads as [] (the itemFollowupStates
 *  / conversionStates optional-array convention above). */
export function rowAdvisedItems(
  row: PatientFollowupRowView,
): readonly PatientFollowupPlanItemView[] {
  return row.advisedItems ?? [];
}

/** ALL advised treatment names (+ price when configured), comma-joined --
 *  same construction as `treatmentNames` but with no "Treatment advised"
 *  fallback: callers only render this when `rowAdvisedItems(row).length >
 *  0`, so an empty input never reaches here in practice. The price rides
 *  per-item (never a row-level total -- `totalEstimateInr(row.openItems)`
 *  is UNCHANGED and stays scoped to D-3 open items only, so an
 *  advised-only row honestly shows "No estimate" in the Estimate column
 *  while still surfacing what an advised item costs here, inline, the same
 *  money grammar as everywhere else on this page). Names + price only (the
 *  same wire fence as `treatmentNames` -- never a clinical note, chief
 *  complaint, diagnosis category, or machine id). */
export function advisedItemsSummary(
  items: readonly PatientFollowupPlanItemView[],
): string {
  return items
    .map((item) =>
      item.estimatedValue !== null
        ? `${item.procedureName} (${formatEstimateInr(item.estimatedValue)})`
        : item.procedureName,
    )
    .join(', ');
}

/** The governed ADVISED chip label (R-STATE-URGENCY-PALETTE-01, matrix row
 *  "AWAITING RESPONSE"): read from the SAME canonical status vocabulary the
 *  doctor consult workspace writes and labels (planItemStatusLabel,
 *  case-sheet-labels.ts) -- no second status label invented for this chip. */
export const ADVISED_CHIP_LABEL = planItemStatusLabel('advised');

/**
 * COC-RENDER-CLOSURE-V1 (activity 16): the AUTHORITATIVE doctor-recorded
 * plan-item decision -- `item.status`/`item.declineReason`, already on this
 * wire, reused verbatim from the SAME canonical vocabulary the doctor
 * consult workspace writes and labels (planItemStatusLabel,
 * case-sheet-labels.ts; outcomeReasonLabel, owner/Lead360Panel.tsx -- no
 * second status/reason vocabulary invented). Distinct from, and never
 * collapsed into, this page's OWN "Last contact" column (the COC's call
 * evidence, terminalStateLabel above): this is what the DOCTOR decided at
 * consult, before any COC follow-up call happened. Null when the item
 * carries no doctor-recorded decline (the open register's items are always
 * pending or declined in practice; pending renders no marker -- the default,
 * unexceptional state needs none, matching this page's own Overdue/CSD
 * absence-is-the-signal idiom).
 */
export function itemDeclineLabel(item: PatientFollowupPlanItemView): string | null {
  if (item.status !== 'declined') return null
  const statusLabel = planItemStatusLabel(item.status);
  return item.declineReason !== null
    ? `${statusLabel} · ${outcomeReasonLabel(item.declineReason)}`
    : statusLabel;
}

/** The row's WHERE sub-line: clinic (+ the treating doctor when known). NEVER a
 *  UMR / uuid / item id / clinical category (identity = name + context). */
export function rowWhereLine(row: PatientFollowupRowView): string {
  const parts: string[] = [];
  if (row.clinicLabel !== null) parts.push(row.clinicLabel);
  if (row.treatingDoctorName !== null) parts.push(row.treatingDoctorName);
  return parts.join(' · ');
}

/** The drawer's identity CONTEXT sub-line: the full treatment names + clinic
 *  (+ treating doctor when known). Same fences as above -- no machine id, no
 *  clinical narrative. */
export function rowContextLine(row: PatientFollowupRowView): string {
  const where = rowWhereLine(row);
  return where === ''
    ? treatmentNames(row.openItems)
    : `${treatmentNames(row.openItems)} · ${where}`;
}

/** The DETERMINISTIC register order, stated on the glass (#593 visual
 *  corrective item 2B): OVERDUE first (past the first-follow-up SLA -- a lawful
 *  COMPUTED state, one of the founder-sanctioned orderings, never an invented
 *  priority score), then LONGEST WAITING (age desc), then name for a stable
 *  tie-break. No money / attempt-count / CSD weighting -- subsequent ranking
 *  law is OPEN (tenant Q, founder ruling), so none is manufactured here. */
export const REGISTER_ORDER_CLAUSE = 'Overdue first, then longest waiting';

export function orderRegisterRows(
  rows: readonly PatientFollowupRowView[],
  statesByItem: Map<number, CocItemFollowupState>,
  slaDays: number | null,
): PatientFollowupRowView[] {
  return [...rows].sort((a, b) => {
    const ao = rowIsOverdue(a, statesByItem, slaDays) ? 0 : 1;
    const bo = rowIsOverdue(b, statesByItem, slaDays) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    if (a.ageDays !== b.ageDays) return b.ageDays - a.ageDays;
    return a.patientName.localeCompare(b.patientName);
  });
}

/** How many START HERE cases the attention lens surfaces at most (the approved
 *  reference shows three: "showing 3"). A lens over the register, never a
 *  second queue -- the same rows REMAIN in the register below (DUPLICATION
 *  LAW, the approved reference header). */
export const START_HERE_LIMIT = 3;

/** The on-glass order statement for the START HERE lens (the ORDERING FENCE:
 *  a lawful already-computed state -- overdue AND not yet contacted -- ordered
 *  by a column already shown, longest waiting first; never money, composite or
 *  inferred). */
export const START_HERE_ORDER_CLAUSE = 'longest waiting first';

/**
 * THE START HERE LENS (approved reference, artboard 1): the overdue-and-nobody-
 * has-called-yet cases -- `rowIsOverdue` is exactly "past the first-follow-up
 * SLA AND no contact recorded" -- ordered LONGEST WAITING FIRST (age desc, then
 * name), capped at `limit`. Pure; a VIEW over the register rows (the same rows
 * stay in the full register -- never deduplicated).
 */
export function startHereRows(
  rows: readonly PatientFollowupRowView[],
  statesByItem: Map<number, CocItemFollowupState>,
  slaDays: number | null,
  limit: number = START_HERE_LIMIT,
): PatientFollowupRowView[] {
  return [...rows]
    .filter((r) => rowIsOverdue(r, statesByItem, slaDays))
    .sort((a, b) => {
      if (a.ageDays !== b.ageDays) return b.ageDays - a.ageDays;
      return a.patientName.localeCompare(b.patientName);
    })
    .slice(0, limit);
}

/** Human label for a per-item terminal state (RULING 2). `converted` never
 *  reaches here -- it is payment-derived and drops the item from the queue. */
export function terminalStateLabel(state: CocItemTerminalState): string {
  switch (state) {
    case 'declined':
      return 'Declined';
    case 'unreachable':
      return 'Unreachable';
    case 'cold':
      return 'Cold';
    case 'open':
    default:
      // "Still open", not "Open" -- founder ruling 2026-09-09 (corrective 1,
      // SETTLED). One fact carries one name on this screen: the drawer's
      // outcome picker and the earlier-contacts timeline already say "Still
      // open" (STATE_OPTIONS / historyEventOutcomeLabel), and the register's
      // LAST CONTACT column said "Open" -- two words for one state, on one
      // screen. This is the register half being brought into line.
      return 'Still open';
  }
}

/** Semantic tone for a per-item terminal state -- colour carries meaning, and
 *  the label always rides beside it (never colour alone). Declined/Unreachable
 *  are the amber "still needs a nudge" outcomes; Cold is a quiet settled
 *  outcome; Open is a neutral fact. */
export function terminalStateTone(state: CocItemTerminalState): AcBadgeTone {
  switch (state) {
    case 'declined':
    case 'unreachable':
      return 'pending';
    case 'cold':
      return 'neutral';
    case 'open':
    default:
      return 'info';
  }
}

// ---------------------------------------------------------------------------
// FOLLOWUP-DUE-DATE-PLUMBING-V1 -- the per-item next follow-up due day
// (tenant grain ruling Deepak 2026-08-21 item 8; carrier migration 20260826_1,
// FOUNDER-APPLIED 2026-08-26). Pure derivations only: the wire carries the
// COC's own recorded calendar day per item; these helpers compare it to the
// IST calendar day (istCalendarDayYmd -- the clinic day, never the browser's
// local calendar) and map the result onto the RATIFIED urgency ramp
// (R-STATE-URGENCY-PALETTE-01; docs/design/design-language/02-DESIGN-TOKENS.md
// 10b.1). The mapping follows the coc-daily-report-view precedent exactly:
// only the two non-neutral steps whose tokens exist in the shipped ramp are
// populated (soon = amber outline-clock, breached = red filled-alert);
// the ratified 'now' step has no shipped token and inventing a third
// threshold here would manufacture a cutoff no ruling gives us. NO
// escalation: the 60-day team-leader threshold (item 9) is a config-owned
// SEPARATE mechanism this surface never derives.
// ---------------------------------------------------------------------------

/** The due standing of one recorded next-follow-up day: not yet due /
 *  due today / past due. Never derived without a recorded date. */
export type FollowupDueState = 'scheduled' | 'due_today' | 'overdue';

/** Compare a recorded bare YYYY-MM-DD due day to the IST calendar day of
 *  `nowMs` (lexicographic compare is exact on this shape). */
export function dueStateForDate(dueDateYmd: string, nowMs: number): FollowupDueState {
  const today = istCalendarDayYmd(nowMs);
  if (dueDateYmd < today) return 'overdue';
  if (dueDateYmd === today) return 'due_today';
  return 'scheduled';
}

/** One item's CURRENT recorded due day (absent-friendly older-payload read:
 *  the itemFollowupStates optional-field convention). */
export function itemDueDate(state: CocItemFollowupState | undefined): string | null {
  return state?.nextFollowupDueDate ?? null;
}

/** The row's next follow-up: the EARLIEST current due day across its open
 *  items (the next action the register should surface), with its standing. */
export interface RowDueInfo {
  readonly dueDate: string;
  readonly state: FollowupDueState;
}

export function rowNextDue(
  row: PatientFollowupRowView,
  statesByItem: Map<number, CocItemFollowupState>,
  nowMs: number,
): RowDueInfo | null {
  let earliest: string | null = null;
  for (const item of row.openItems) {
    const due = itemDueDate(statesByItem.get(item.id));
    if (due === null) continue;
    if (earliest === null || due < earliest) earliest = due;
  }
  return earliest === null
    ? null
    : { dueDate: earliest, state: dueStateForDate(earliest, nowMs) };
}

/** Map a due standing onto the ratified urgency ramp steps that exist in the
 *  shipped tokens (the urgencyStepForSeverity precedent): a future day is
 *  calm ('none' -- absence of decoration IS the treatment), due today is
 *  'soon', past due is 'breached'. */
export type DueUrgencyStep = 'none' | 'soon' | 'breached';

export function dueUrgencyStep(state: FollowupDueState): DueUrgencyStep {
  if (state === 'overdue') return 'breached';
  if (state === 'due_today') return 'soon';
  return 'none';
}

/** The always-printed due text (the ramp's own law: the value text always
 *  prints; colour and icon are never the only carrier): "Due today" on the
 *  day, otherwise "Due <day> <Mon>" through the ONE short-date owner. */
export function dueDateLabel(due: RowDueInfo): string {
  return due.state === 'due_today'
    ? 'Due today'
    : `Due ${formatIstShortDate(due.dueDate)}`;
}

/** The full-meaning hover/assistive text (the label alone stays compact). */
export function dueDateTitle(due: RowDueInfo): string {
  if (due.state === 'overdue') return 'Next follow-up overdue';
  if (due.state === 'due_today') return 'Next follow-up due today';
  return 'Next follow-up scheduled';
}

/** Human label for the founder-CONFIRMED non-conversion reason (E1). */
export function reasonLabel(reason: CocFollowupReason): string {
  return reason === 'cost' ? 'Cost' : 'Other';
}

/** The single most-recent contact across a row's items -- the "what happened
 *  last" line (founder ruling 8: named actor + time + per-item state, in human
 *  terms; the raw actor principal never reaches the client). Null when no
 *  follow-up has been recorded on any item yet. */
export interface LatestContact {
  readonly atIso: string | null;
  readonly actor: string | null;
  readonly terminalState: CocItemTerminalState;
  readonly reason: CocFollowupReason | null;
  readonly treatmentName: string;
}

export function latestContact(
  row: PatientFollowupRowView,
  statesByItem: Map<number, CocItemFollowupState>,
): LatestContact | null {
  let best: LatestContact | null = null;
  for (const item of row.openItems) {
    const state = statesByItem.get(item.id);
    if (state == null || state.lastContactAtIso == null) continue;
    if (best === null || state.lastContactAtIso > (best.atIso ?? '')) {
      best = {
        atIso: state.lastContactAtIso,
        // supervisor-safe display label only (RULING 7); the raw principal is
        // never on this wire. Falls back to the role when unlabelled.
        actor: state.lastActorLabel ?? state.lastActorRole,
        terminalState: state.terminalState,
        reason: state.reason,
        treatmentName: item.procedureName,
      };
    }
  }
  return best;
}

/** One open item's in-flight draft inside the recorder: whether the COC is
 *  dispositioning it in THIS contact and, if so, the chosen terminal
 *  classification ('' = discussed but left OPEN, RULING 2) plus the Cost/Other
 *  reason shown only under a decline (RULING 3) and the hand-picked next
 *  follow-up day ('' = none) offered only while the item stays OPEN
 *  (FOLLOWUP-DUE-DATE-PLUMBING-V1; grain ruling 2026-08-21 item 8). */
export interface ItemDraft {
  readonly selected: boolean;
  readonly disposition: CocFollowupDisposition | '';
  readonly reason: CocFollowupReason | '';
  readonly nextFollowupDate: string;
}

export const EMPTY_DRAFT: ItemDraft = {
  selected: false,
  disposition: '',
  reason: '',
  nextFollowupDate: '',
};

/**
 * Build the record-contact item payload from the per-item drafts (RULING 6:
 * one contact, many items). ONLY selected items ride; a selected item with no
 * disposition is an ordinary attempt that leaves the item OPEN; a reason rides
 * ONLY a decline (RULING 3) -- a stale Cost/Other under a non-decline is
 * dropped, never sent. The next follow-up day rides ONLY an item left OPEN
 * (no disposition -- the write model refuses it elsewhere): a stale date
 * under a terminal pick is likewise dropped, never sent.
 */
export function buildContactItems(
  drafts: ReadonlyMap<number, ItemDraft>,
): RecordCocFollowupItemArg[] {
  const out: RecordCocFollowupItemArg[] = [];
  for (const [id, draft] of drafts) {
    if (!draft.selected) continue;
    const item: {
      treatmentPlanItemId: number;
      disposition?: CocFollowupDisposition;
      reason?: CocFollowupReason;
      nextFollowupDueDate?: string;
    } = { treatmentPlanItemId: id };
    if (draft.disposition !== '') {
      item.disposition = draft.disposition;
      if (draft.disposition === 'declined' && draft.reason !== '') {
        item.reason = draft.reason;
      }
    } else if (draft.nextFollowupDate !== '') {
      item.nextFollowupDueDate = draft.nextFollowupDate;
    }
    out.push(item);
  }
  return out;
}

/** How many items the COC has selected to disposition in this contact. */
export function selectedItemCount(drafts: ReadonlyMap<number, ItemDraft>): number {
  let n = 0;
  for (const draft of drafts.values()) if (draft.selected) n += 1;
  return n;
}

// ---------------------------------------------------------------------------
// FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1 -- caller-relative ownership rendering
// (tenant ruling PARTHA-FOLLOWUP-OWNERSHIP-2026-08-21.md R2: a viewer can SEE
// where a case sits and do NOTHING to it). The server annotates each row with
// the ownership truth it enforced; these helpers only RENDER it. A row with
// no annotation (an older payload) stays actionable -- the pre-ownership
// behavior (fail-same).
// ---------------------------------------------------------------------------

/** May THIS caller record a contact on this row? Server-derived; absence of
 *  the annotation = the legacy single-audience behavior (actionable). */
export function rowMayAct(row: PatientFollowupRowView): boolean {
  return row.followupOwnership?.mayAct !== false;
}

/** Display label for a follow-up owner role token. Mirrors the routing
 *  settings ROLE_LABELS vocabulary for the tokens this surface can meet;
 *  humanized fallback for anything else (labels only -- the tokens come
 *  from the server). */
const OWNER_ROLE_LABELS: Readonly<Record<string, string>> = {
  receptionist: 'Front desk',
  coc: 'COC',
  regional_head: 'Regional head',
  gm_agm: 'GM / AGM',
  owner: 'Owner',
  call_center_agent: 'Call center',
};

export function followupOwnerLabel(ownerRole: string): string {
  const known = OWNER_ROLE_LABELS[ownerRole];
  if (known) return known;
  const spaced = ownerRole.replace(/_/g, ' ').trim();
  return spaced.length > 0
    ? spaced.charAt(0).toUpperCase() + spaced.slice(1)
    : ownerRole;
}

/** The view-only marker for a row the caller cannot act on: names WHO owns
 *  the case now (a DISPLAY flag only -- the ruling's remedy is human:
 *  telling the owner; no product mechanism exists). Empty string when the
 *  caller may act (no marker rendered). */
export function rowViewOnlyLabel(row: PatientFollowupRowView): string {
  if (rowMayAct(row)) return '';
  const owner = row.followupOwnership?.ownerRole;
  return owner !== undefined
    ? `With ${followupOwnerLabel(owner)} - view only`
    : 'View only';
}

// ---------------------------------------------------------------------------
// COC-FOLLOWUP-OWNERSHIP-SEAT-V1 -- the owner named in EVERY row.
// Before this package a row named its owner ONLY when the caller was locked
// out of it (rowViewOnlyLabel above). A row the caller may act on said nothing
// about who holds it or on which day it moves -- so a front-desk queue never
// said when a case stops being the front desk's, and a supervisor (owner /
// GM, every row actionable) saw no owner anywhere. The founder constraint
// this seats: an ownership STATE is visible IN THE ROW, never buried.
// Nothing here is a second rule: the phase, the owner and the boundary all
// arrive on the wire from the ONE decision home
// (lib/domain/patient/followup-ownership.ts); these helpers only RENDER them.
// ---------------------------------------------------------------------------

/** TRUE when ownership is a LIVE distinction on this org: a real window
 *  (boundary > 0) whose two sides belong to different roles. Under the seeded
 *  DEFAULT config (one owner, zero-width window) every row would carry the
 *  same word, so nothing is rendered -- that org's screen stays byte-identical
 *  (PRESERVATION FIRST). An older payload without the config block reads as
 *  not live. */
export function followupOwnershipIsLive(
  config: PatientFollowupQueueView['followupOwnership'],
): boolean {
  return (
    config !== undefined &&
    config.boundaryDays > 0 &&
    config.ownerBeforeBoundary !== config.ownerAfterBoundary
  );
}

/** The one ownership line for a row the caller MAY act on: who holds it and
 *  the day it moves -- "With Front desk until day 30" before the boundary,
 *  "With COC from day 30" after. Null (nothing rendered) when ownership is not
 *  a live distinction on this org, when the row carries no annotation (an
 *  older payload), or when the caller may NOT act -- that row already names
 *  its owner in the action column (rowViewOnlyLabel), and the owner is said
 *  exactly once per row, never twice. */
export function rowOwnershipLine(
  row: PatientFollowupRowView,
  config: PatientFollowupQueueView['followupOwnership'],
): string | null {
  if (!followupOwnershipIsLive(config) || config === undefined) return null;
  const ownership = row.followupOwnership;
  if (ownership === undefined || !ownership.mayAct) return null;
  const owner = followupOwnerLabel(ownership.ownerRole);
  return ownership.phase === 'before_boundary'
    ? `With ${owner} until day ${config.boundaryDays}`
    : `With ${owner} from day ${config.boundaryDays}`;
}

// --- THE READ-ONLY LENS (Phase 2 Slice 1) ----------------------------------

/**
 * WHICH LENS OF THE SAME REGISTER THIS CALLER IS LOOKING THROUGH.
 *
 *  - 'work'      -- the caller can record contacts on the cases it owns (the
 *                   COC, and the unchanged non-party supervisor chain). The
 *                   register carries its action column.
 *  - 'read_only' -- the caller holds the org's WINDOW-READ grant and is not a
 *                   configured ownership party: it SEES where a case sits and
 *                   can act on NOTHING (Partha's front desk, R2's "they can
 *                   do NOTHING to it"). The register carries NO action column.
 *
 * THIS IS NOT A SECOND RULE. It mirrors, clause for clause, the server's own
 * `resolveFollowupCaseRelation` (lib/domain/patient/followup-ownership.ts):
 * an ownership PARTY resolves to 'owner' before the window-read grant is ever
 * consulted, so the two orderings cannot disagree. The inputs are the wire's
 * own config block -- `windowReadRoles` and the two owner roles the response
 * states it was composed under -- never a role list authored on the client.
 *
 * AND IT IS NOT THE FENCE. The write fence is server-side and unchanged: the
 * same `mayActOnFollowupCase` derivation refuses the POST regardless of what
 * this function returns (lib/adapters/supabase/coc-followup-attempt-write-
 * model.ts, the per-case ownership gate). Removing a button is a courtesy to
 * the reader, never the boundary -- LAW 2, route fence is not field fence.
 *
 * A payload with no config block (an older backend) resolves to 'work', which
 * is the pre-ownership behaviour every existing caller already gets from
 * `rowMayAct`: fail-same, never wider, and never a client-invented denial.
 */
export type FollowupLens = 'work' | 'read_only';

export function resolveFollowupLens(
  callerRole: string | null | undefined,
  config: PatientFollowupQueueView['followupOwnership'],
): FollowupLens {
  if (config === undefined || callerRole === null || callerRole === undefined) {
    return 'work';
  }
  // An ownership PARTY is an owner of its own phase -- it acts there, so it
  // keeps the work lens even when it also appears in the read grant.
  if (
    callerRole === config.ownerBeforeBoundary ||
    callerRole === config.ownerAfterBoundary
  ) {
    return 'work';
  }
  return config.windowReadRoles.includes(callerRole) ? 'read_only' : 'work';
}

/**
 * THE "LAST CONTACT" CELL, as one value.
 *
 * The shipped register prints a DASH until the row is contacted and the
 * outcome + safe named actor afterwards; this returns the same two facts for
 * the restored column (R5 item 4) so the cell has ONE construction whichever
 * lens renders it. Null = never contacted, which the caller renders as the
 * dash -- the fact has one name on every viewport (R5 B2).
 */
export function rowLastContactCell(
  row: PatientFollowupRowView,
  statesByItem: Map<number, CocItemFollowupState>,
): { readonly outcome: string; readonly meta: string; readonly declined: boolean } | null {
  const last = latestContact(row, statesByItem);
  if (last === null) return null;
  const reasonPart = last.reason !== null ? ` · ${reasonLabel(last.reason)}` : '';
  const actorPart = last.actor !== null ? `${last.actor} · ` : '';
  return {
    outcome: `${terminalStateLabel(last.terminalState)}${reasonPart}`,
    meta: `${actorPart}${formatIstShortDate(last.atIso!)}`,
    declined: last.terminalState === 'declined',
  };
}

/**
 * EVERY SELECTED ITEM MARKED "Declined" MUST CARRY ITS WHY (R5 frame 04).
 *
 * Returns the ids of selected declined drafts with no reason picked. The
 * server permits a reason ONLY under a decline and refuses it anywhere else
 * (lib/domain/patient/coc-followup-attempt.ts, RULING 3) -- that half is
 * already a server fence with its own test. The other half, "a decline must
 * SAY why", has no server rule today: this screen enforces it at the save,
 * and the run record files the server-side hardening as a proposal with its
 * cost rather than widening a shared domain contract inside this slice.
 */
export function declinesMissingReason(
  drafts: ReadonlyMap<number, ItemDraft>,
): number[] {
  const out: number[] = [];
  for (const [id, draft] of drafts) {
    if (draft.selected && draft.disposition === 'declined' && draft.reason === '') {
      out.push(id);
    }
  }
  return out;
}
